import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { PageHeader } from '@/components/AIBadge';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    CreditCard, TrendingUp, AlertTriangle, CheckCircle2, Clock, XCircle, Search, Loader2,
} from 'lucide-react';

const money = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);

const STATUS_META = {
    paid: { label: 'Pagado', color: '#00875A', icon: CheckCircle2 },
    unpaid: { label: 'Pendiente', color: '#F59E0B', icon: Clock },
    initiated: { label: 'Iniciado', color: '#6B7280', icon: Clock },
    failed: { label: 'Fallido', color: '#EF4444', icon: XCircle },
    expired: { label: 'Expirado', color: '#6B7280', icon: XCircle },
};

const TYPE_LABEL = {
    audit_monthly: 'Iguala mensual',
    cycle_upfront: 'Contado ciclo',
    cycle_renewal: 'Renovación anual',
};

export default function AdminPayments() {
    const [data, setData] = useState({ transactions: [], kpis: {} });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        api.get('/admin/payments')
            .then((r) => setData(r.data))
            .catch((e) => toast.error(apiErrorMessage(e, 'Error al cargar pagos')))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const query = q.trim().toLowerCase();
        return data.transactions.filter((t) => {
            const ps = t.payment_status === 'paid' ? 'paid' : (t.status === 'expired' ? 'expired' : t.payment_status);
            if (statusFilter !== 'all' && ps !== statusFilter) return false;
            if (query) {
                const hay = `${t.client?.company || ''} ${t.client?.name || ''} ${t.client?.email || ''} ${t.label || ''}`.toLowerCase();
                if (!hay.includes(query)) return false;
            }
            return true;
        });
    }, [data.transactions, q, statusFilter]);

    const k = data.kpis || {};

    return (
        <AdminLayout title="Pagos">
            <div className="max-w-[1400px] space-y-6">
                <PageHeader
                    icon={CreditCard}
                    title="Pagos e ingresos"
                    subtitle="Historial de transacciones vía Stripe. Estado por ciclo y modalidad."
                    accent="#635BFF"
                    data-testid="admin-payments-header"
                />

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    <KpiBox label="Total cobrado" value={money(k.total_paid_mxn)} color="#00875A" icon={TrendingUp} large />
                    <KpiBox label="Pendiente" value={money(k.total_pending_mxn)} color="#F59E0B" icon={Clock} large />
                    <KpiBox label="Fallido / Expirado" value={money(k.total_failed_mxn)} color="#EF4444" icon={AlertTriangle} large />
                    <KpiBox label="Pagos exitosos" value={k.count_paid || 0} color="#00875A" icon={CheckCircle2} />
                    <KpiBox label="Pagos pendientes" value={k.count_pending || 0} color="#F59E0B" icon={Clock} />
                </div>

                {/* Filtros */}
                <div className="flex flex-wrap gap-2 items-center">
                    <div className="flex items-center border-2 px-2 py-1 flex-1 min-w-[200px]"
                        style={{ borderColor: 'var(--border)' }}>
                        <Search className="w-4 h-4 opacity-60" />
                        <input type="text" placeholder="Buscar por cliente o concepto..." value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="payments-search-input"
                            className="bg-transparent outline-none px-2 py-1 flex-1 text-sm" />
                    </div>
                    <div className="flex gap-1">
                        {[
                            { v: 'all', label: 'Todos' },
                            { v: 'paid', label: 'Pagados' },
                            { v: 'unpaid', label: 'Pendientes' },
                            { v: 'expired', label: 'Expirados' },
                        ].map((f) => (
                            <button key={f.v} onClick={() => setStatusFilter(f.v)}
                                data-testid={`payments-filter-${f.v}`}
                                className="text-xs font-bold uppercase tracking-widest px-3 py-2 border-2"
                                style={{
                                    background: statusFilter === f.v ? '#111' : 'transparent',
                                    color: statusFilter === f.v ? '#fff' : 'inherit',
                                    borderColor: 'var(--border)',
                                }}>
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tabla */}
                {loading ? (
                    <div className="p-12 text-center opacity-60">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </div>
                ) : (
                    <div className="border overflow-x-auto" style={{ borderColor: 'var(--border)' }} data-testid="payments-table">
                        <table className="w-full text-sm min-w-[900px]">
                            <thead>
                                <tr style={{ background: '#111', color: '#fff' }}>
                                    <th className="text-left p-3 text-[10px] uppercase tracking-widest font-bold">Fecha</th>
                                    <th className="text-left p-3 text-[10px] uppercase tracking-widest font-bold">Cliente</th>
                                    <th className="text-left p-3 text-[10px] uppercase tracking-widest font-bold">Concepto</th>
                                    <th className="text-left p-3 text-[10px] uppercase tracking-widest font-bold">Tipo</th>
                                    <th className="text-right p-3 text-[10px] uppercase tracking-widest font-bold">Monto</th>
                                    <th className="text-center p-3 text-[10px] uppercase tracking-widest font-bold">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((t) => {
                                    const ps = t.payment_status === 'paid' ? 'paid' : (t.status === 'expired' ? 'expired' : t.payment_status);
                                    const meta = STATUS_META[ps] || STATUS_META.unpaid;
                                    const Icn = meta.icon;
                                    return (
                                        <tr key={t.id} className="border-t hover:bg-black/[0.02]"
                                            style={{ borderColor: 'var(--border)' }}
                                            data-testid={`payment-row-${t.id}`}>
                                            <td className="p-3 text-xs">
                                                {new Date(t.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                                            </td>
                                            <td className="p-3">
                                                <div className="font-semibold">{t.client?.company || t.client?.name || '—'}</div>
                                                <div className="text-[10px] opacity-60">{t.client?.email}</div>
                                            </td>
                                            <td className="p-3 text-xs">{t.label || 'Pago'}</td>
                                            <td className="p-3 text-xs">
                                                <span className="font-bold">{TYPE_LABEL[t.type] || t.type}</span>
                                                <div className="text-[10px] opacity-60">{t.package_code}</div>
                                            </td>
                                            <td className="p-3 text-right font-mono font-bold">{money(t.amount_mxn)}</td>
                                            <td className="p-3 text-center">
                                                <div className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1"
                                                    style={{ background: `${meta.color}15`, color: meta.color }}>
                                                    <Icn className="w-3 h-3" />
                                                    {meta.label}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="p-8 text-center opacity-60">
                                            Sin transacciones aún. Los pagos aparecerán aquí conforme los clientes vayan pagando.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

function KpiBox({ label, value, color, icon: Icon, large }) {
    return (
        <div className="p-3 border" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-1.5 mb-1">
                {Icon && <Icon className="w-3.5 h-3.5" style={{ color }} />}
                <span className="text-[10px] uppercase tracking-widest font-bold opacity-60">{label}</span>
            </div>
            <div className={`font-display font-black ${large ? 'text-xl' : 'text-2xl'}`} style={{ color }}>{value}</div>
        </div>
    );
}
